import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR, formatDate, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { AttachmentField, AttachmentLink } from "@/components/TransactionAttachment";
import { RetailExportButtons, soldQuantity } from "@/components/RetailExportButtons";
import DateRangeFilter, { useDateFilter } from "@/components/DateRangeFilter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Store as StoreIcon, Package, ChevronRight, LayoutList, LayoutGrid, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTableFilter } from "@/lib/useTableFilter";
import { SortHeader, TableToolbar } from "@/components/TableTools";

export default function StoreSales() {
  const nav = useNavigate();
  const [stores, setStores] = useState([]);
  const [storeStocks, setStoreStocks] = useState([]); // stok titipan semua toko
  const [items, setItems] = useState([]); // riwayat store_sales
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const [form, setForm] = useState({ store_id: "", payment_method: "cash", notes: "", date: "", lines: [] });
  const [availableStock, setAvailableStock] = useState([]); // stock at chosen store
  const [editingId, setEditingId] = useState(null); // ID sale yang sedang di-edit

  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });
  const historyRows = useMemo(() => items.map(row => ({ ...row, sold_qty: soldQuantity(row) })), [items]);
  const filteredHistory = useDateFilter(historyRows, range);
  const invalidRange = Boolean(range.from && range.to && range.from > range.to);
  const tf = useTableFilter(filteredHistory, {
    searchKeys: ["store_name", "payment_method", "notes"],
    defaultSort: { key: "date", dir: "desc" },
  });
  const [viewMode, setViewMode] = useState("list"); // list | detail
  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/stores"),
      api.get("/store-stock"),
      api.get("/store-sales"),
      api.get("/products"),
    ]);
    setStores(a.data); setStoreStocks(b.data); setItems(c.data); setProducts(d.data);
  };
  useEffect(() => { load(); }, []);

  // Aggregate per store: total qty titipan, total nilai
  const perStore = useMemo(() => {
    const map = {};
    for (const s of stores) map[s.id] = { store: s, store_name: s.name, contact: s.contact || "", qty: 0, value: 0, items: 0, has_stock: false };
    for (const ss of storeStocks) {
      if (!map[ss.store_id]) continue;
      map[ss.store_id].qty += Number(ss.qty || 0);
      map[ss.store_id].value += Number(ss.qty || 0) * Number(ss.unit_price || 0);
      map[ss.store_id].items += 1;
    }
    return Object.values(map).map(r => ({ ...r, has_stock: r.qty > 0 }));
  }, [stores, storeStocks]);

  const storeTF = useTableFilter(perStore, {
    searchKeys: ["store_name", "contact"],
    defaultSort: { key: "qty", dir: "desc" },
  });

  useEffect(() => {
    if (form.store_id) {
      const stk = storeStocks.filter((s) => s.store_id === form.store_id);
      setAvailableStock(stk);
    } else setAvailableStock([]);
  }, [form.store_id, storeStocks]);

  const openNew = (store_id = "") => {
    setEditingId(null);
    setAttachment(null);
    setForm({ store_id, payment_method: "cash", notes: "", date: new Date().toISOString().slice(0,10), lines: [{ product_id: "", qty: 1, unit_price: 0 }] });
    setOpen(true);
  };

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", qty: 1, unit_price: 0 }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, key, val) => setForm((f) => {
    const lines = [...f.lines]; lines[i] = { ...lines[i], [key]: val };
    if (key === "product_id") {
      const ss = availableStock.find((s) => s.product_id === val);
      if (ss) lines[i].unit_price = ss.unit_price;
    }
    return { ...f, lines };
  });

  const total = useMemo(() => form.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_price || 0), 0), [form.lines]);

  const save = async () => {
    if (saving || uploading) return;
    if (!form.store_id) return toast.error("Pilih toko");
    if (form.lines.length === 0 || form.lines.some((l) => !l.product_id || Number(l.qty) <= 0 || Number(l.unit_price) < 0))
      return toast.error("Lengkapi item dengan kuantitas positif dan harga tidak negatif");
    setSaving(true);
    try {
      const payload = {
        attachment_id: attachment?.id || null,
        store_id: form.store_id,
        payment_method: form.payment_method,
        notes: form.notes,
        date: form.date || undefined,
        items: form.lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty), unit_price: Number(l.unit_price) })),
      };
      if (editingId) {
        await api.put(`/store-sales/${editingId}`, payload);
        toast.success("Laporan diperbarui. Stok toko & kas disesuaikan.");
      } else {
        await api.post("/store-sales", payload);
        toast.success("Laporan tersimpan. Stok toko berkurang & pembayaran masuk kas.");
      }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const openEdit = async (s) => {
    setEditingId(s.id);
    setAttachment(s.attachment || null);
    setForm({
      store_id: s.store_id,
      payment_method: s.payment_method || "cash",
      notes: s.notes || "",
      date: (s.date || "").slice(0, 10),
      lines: (s.items || []).map((it) => ({
        product_id: it.product_id, qty: it.qty, unit_price: it.unit_price,
      })),
    });
    // Load available stock: stok saat ini + qty dari sale yang sedang di-edit (karena akan dikembalikan)
    try {
      const { data } = await api.get(`/stores/${s.store_id}/stock`);
      const stocksNow = data.items.map((it) => ({
        product_id: it.product_id, product_name: it.product_name,
        qty: it.qty, unit_price: it.unit_price,
      }));
      // Tambahkan qty dari items sale yang di-edit
      for (const it of (s.items || [])) {
        const idx = stocksNow.findIndex((x) => x.product_id === it.product_id);
        if (idx >= 0) stocksNow[idx].qty += it.qty;
        else stocksNow.push({ product_id: it.product_id, product_name: it.product_name, qty: it.qty, unit_price: it.unit_price });
      }
      setAvailableStock(stocksNow.filter((x) => x.qty > 0));
    } catch (e) { setAvailableStock([]); }
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Hapus laporan? Stok toko dikembalikan.")) return;
    try { await api.delete(`/store-sales/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHeader
        title="Laporan Penjualan Retail"
        subtitle="Toko lapor hasil jualan → stok titipan berkurang, pembayaran masuk otomatis ke arus kas"
        testId="page-store-sales"
        actions={
          <div className="flex gap-2">
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button onClick={() => setViewMode("list")} data-testid="view-list" className={`px-3 h-9 text-xs inline-flex items-center gap-1 ${viewMode==="list"?"bg-[var(--hotto-primary)] text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>
                <LayoutList className="w-3 h-3"/> List
              </button>
              <button onClick={() => setViewMode("detail")} data-testid="view-detail" className={`px-3 h-9 text-xs inline-flex items-center gap-1 border-l border-slate-200 ${viewMode==="detail"?"bg-[var(--hotto-primary)] text-white":"bg-white text-slate-600 hover:bg-slate-50"}`}>
                <LayoutGrid className="w-3 h-3"/> Detail
              </button>
            </div>
            <Button onClick={()=>openNew()} data-testid="btn-add-store-sale" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
              <Plus className="w-4 h-4 mr-2"/> Terima Laporan
            </Button>
          </div>
        }
      />

      {/* Stok Titipan Per Toko */}
      <div className="mb-8">
        <div className="overline mb-3">Stok Titipan di Toko</div>
        {perStore.length === 0 ? (
          <Card className="p-6 border border-[var(--hotto-border)] rounded-md text-slate-500 text-sm text-center">Belum ada toko binaan.</Card>
        ) : (
          <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
            <div className="p-3 border-b border-[var(--hotto-border)]">
              <TableToolbar tf={storeTF} placeholder="Cari toko / kontak..." testId="stock-store-search"/>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10">#</TableHead>
                  <SortHeader tf={storeTF} col="store_name">Toko</SortHeader>
                  <SortHeader tf={storeTF} col="contact">Kontak</SortHeader>
                  <SortHeader tf={storeTF} col="items" className="text-right">Jenis</SortHeader>
                  <SortHeader tf={storeTF} col="qty" className="text-right">Stok</SortHeader>
                  <SortHeader tf={storeTF} col="value" className="text-right">Nilai (harga toko)</SortHeader>
                  <SortHeader tf={storeTF} col="has_stock">Status</SortHeader>
                  <TableHead className="w-40 text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {storeTF.rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Tidak ada toko yang cocok.</TableCell></TableRow>}
                {storeTF.rows.map((row, idx) => {
                  const hasStock = row.has_stock;
                  return (
                    <TableRow
                      key={row.store.id}
                      className="table-row-hover cursor-pointer"
                      onClick={() => nav(`/stores/${row.store.id}`)}
                      data-testid={`store-row-${row.store.id}`}
                    >
                      <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center ${hasStock ? "bg-emerald-100" : "bg-slate-200"}`}>
                            <StoreIcon className={`w-3.5 h-3.5 ${hasStock ? "text-[var(--hotto-primary)]" : "text-slate-500"}`}/>
                          </div>
                          <span style={{ fontFamily: 'Outfit' }}>{row.store.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{row.contact || "-"}</TableCell>
                      <TableCell className="text-right font-mono-num text-sm text-slate-600">{row.items}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${hasStock ? "text-slate-900" : "text-slate-400"}`}>{row.qty}</TableCell>
                      <TableCell className={`text-right font-mono-num font-semibold ${hasStock ? "text-[var(--hotto-primary)]" : "text-slate-400"}`}>{formatIDR(row.value)}</TableCell>
                      <TableCell>
                        {hasStock ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px] uppercase tracking-wider">Ada Stok</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-slate-500">Kosong</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant={hasStock ? "default" : "outline"}
                          size="sm"
                          onClick={() => openNew(row.store.id)}
                          className={hasStock ? "bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)] h-7 text-xs" : "h-7 text-xs"}
                          disabled={!hasStock}
                          data-testid={`btn-report-${row.store.id}`}
                        >
                          {hasStock ? "Terima Laporan" : "Belum Ada"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Riwayat Laporan */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="overline">Riwayat Laporan Penjualan</div>
        <RetailExportButtons rows={tf.rows} from={range.from} to={range.to} search={tf.search} title="Laporan Penjualan Retail" testId="reports"/>
      </div>
      <div className="mb-3"><DateRangeFilter value={range} onChange={setRange}/></div>
      <p className="text-xs text-slate-500 mb-3" data-testid="report-export-hint">Unduhan mengikuti rentang tanggal dan pencarian riwayat di bawah, bukan stok titipan.</p>
      {invalidRange && <p role="alert" className="text-sm text-red-600 mb-3" data-testid="report-date-error">Tanggal awal tidak boleh melewati tanggal akhir.</p>}
      <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
        <div className="p-3 border-b border-[var(--hotto-border)]">
          <TableToolbar tf={tf} placeholder="Cari toko / metode..." testId="storesales-search"/>
        </div>
        {viewMode === "list" ? (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">#</TableHead>
                <SortHeader tf={tf} col="date">Tanggal</SortHeader>
                <SortHeader tf={tf} col="store_name">Toko</SortHeader>
                <TableHead>Item</TableHead>
                <SortHeader tf={tf} col="sold_qty" className="text-right">Kuantitas Terjual</SortHeader>
                <TableHead>Lampiran</TableHead>
                <SortHeader tf={tf} col="payment_method">Metode Bayar</SortHeader>
                <SortHeader tf={tf} col="total" className="text-right">Total Diterima</SortHeader>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tf.rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-500">Belum ada laporan.</TableCell></TableRow>}
              {tf.rows.map((s, idx) => (
                <TableRow key={s.id} className="table-row-hover">
                  <TableCell className="font-mono-num text-slate-400 text-sm">{idx + 1}</TableCell>
                  <TableCell className="font-mono-num text-sm">{formatDate(s.date)}</TableCell>
                  <TableCell className="font-medium">{s.store_name}</TableCell>
                  <TableCell className="text-slate-500">{s.items.length} jenis</TableCell>
                  <TableCell className="text-right font-mono-num" data-testid={`sale-quantity-${s.id}`}>{s.sold_qty}</TableCell>
                  <TableCell><AttachmentLink attachment={s.attachment} testId={`sale-attachment-${s.id}`}/></TableCell>
                  <TableCell><Badge variant="outline" className="uppercase text-xs">{s.payment_method}</Badge></TableCell>
                  <TableCell className="text-right font-mono-num font-semibold text-[var(--hotto-success)]">{formatIDR(s.total)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={()=>setDetail(s)}><ChevronRight className="w-4 h-4"/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>openEdit(s)} data-testid={`btn-edit-sale-${s.id}`}><Pencil className="w-4 h-4"/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>del(s.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {tf.rows.length === 0 && <div className="col-span-full text-center py-8 text-slate-500">Belum ada laporan.</div>}
            {tf.rows.map((s) => (
              <Card key={s.id} className="p-4 border border-slate-200 hover:border-[var(--hotto-primary)] hover:shadow-sm transition-all rounded-md">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs text-slate-500 font-mono-num">{formatDate(s.date)}</div>
                    <div className="font-bold text-sm mt-1" style={{fontFamily:'Outfit'}}>{s.store_name}</div>
                  </div>
                  <Badge variant="outline" className="uppercase text-[10px]">{s.payment_method}</Badge>
                </div>
                <div className="text-xs text-slate-600 space-y-0.5 mb-2 border-t border-slate-100 pt-2">
                  {(s.items || []).slice(0, 3).map((it, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="truncate">{it.qty} × {it.product_name || "?"}</span>
                      <span className="font-mono-num text-slate-500">{formatIDR((it.qty || 0) * (it.unit_price || 0))}</span>
                    </div>
                  ))}
                  {(s.items || []).length > 3 && <div className="italic text-slate-400 text-[11px]">+ {s.items.length - 3} item lain</div>}
                </div>
                <div className="flex items-center justify-between text-xs mb-2"><span data-testid={`sale-card-quantity-${s.id}`}>Kuantitas terjual: {s.sold_qty}</span><AttachmentLink attachment={s.attachment} testId={`sale-card-attachment-${s.id}`}/></div>
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div className="font-mono-num font-bold text-[var(--hotto-success)]">{formatIDR(s.total)}</div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>setDetail(s)} className="h-7 px-2 text-xs">Detail</Button>
                    <Button size="icon" variant="ghost" onClick={()=>openEdit(s)} className="h-7 w-7"><Pencil className="w-3 h-3"/></Button>
                    <Button size="icon" variant="ghost" onClick={()=>del(s.id)} className="h-7 w-7"><Trash2 className="w-3 h-3 text-red-500"/></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Form Dialog */}
      <Dialog open={open} onOpenChange={(value) => { if (!saving && !uploading) setOpen(value); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="store-sale-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit Laporan Penjualan Toko" : "Terima Laporan Penjualan Toko"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Toko {editingId && <span className="text-xs text-slate-400">(tidak bisa diubah)</span>}</Label>
                <Select value={form.store_id} onValueChange={(v)=>setForm({...form,store_id:v,lines:[{product_id:"",qty:1,unit_price:0}]})} disabled={!!editingId}>
                  <SelectTrigger data-testid="select-store"><SelectValue placeholder="Pilih toko"/></SelectTrigger>
                  <SelectContent>{stores.map((s)=><SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tanggal</Label>
                <Input type="date" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} data-testid="input-storesale-date"/>
              </div>
              <div>
                <Label>Metode Bayar</Label>
                <Select value={form.payment_method} onValueChange={(v)=>setForm({...form,payment_method:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Tunai</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="qris">QRIS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.store_id && availableStock.length === 0 && (
              <div className="p-4 bg-orange-50 border border-orange-200 text-orange-700 text-sm rounded-md">
                Belum ada stok titipan di toko ini. Silakan lakukan penyaluran dulu.
              </div>
            )}

            <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Produk (dari stok titipan)</TableHead>
                    <TableHead className="w-24">Qty Terjual</TableHead>
                    <TableHead className="w-36">Harga Toko</TableHead>
                    <TableHead className="w-36 text-right">Subtotal</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {form.lines.map((l, i) => {
                    const stock = availableStock.find((s)=>s.product_id===l.product_id);
                    const productName = (products.find((p)=>p.id===l.product_id) || {}).name;
                    return (
                    <TableRow key={i}>
                      <TableCell>
                        <Select value={l.product_id} onValueChange={(v)=>updateLine(i,"product_id",v)}>
                          <SelectTrigger data-testid={`select-product-${i}`}><SelectValue placeholder="Pilih produk"/></SelectTrigger>
                          <SelectContent>
                            {availableStock.map((ss)=>{
                              const p = products.find((pp)=>pp.id===ss.product_id);
                              return <SelectItem key={ss.product_id} value={ss.product_id}>{p?.name || ss.product_name} · titipan {ss.qty}</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                        {stock && <div className="text-xs text-slate-500 mt-1">Sisa titipan: {stock.qty}</div>}
                      </TableCell>
                      <TableCell><Input type="number" value={l.qty} onChange={(e)=>updateLine(i,"qty",e.target.value)} max={stock?.qty} data-testid={`input-qty-${i}`}/></TableCell>
                      <TableCell><Input type="number" value={l.unit_price} onChange={(e)=>updateLine(i,"unit_price",e.target.value)}/></TableCell>
                      <TableCell className="text-right font-mono-num">{formatIDR(Number(l.qty||0)*Number(l.unit_price||0))}</TableCell>
                      <TableCell><Button size="icon" variant="ghost" onClick={()=>removeLine(i)}><Trash2 className="w-4 h-4 text-red-500"/></Button></TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
              <div className="p-3 border-t border-[var(--hotto-border)] flex justify-between items-center bg-slate-50/50">
                <Button variant="outline" size="sm" onClick={addLine} disabled={!form.store_id}><Plus className="w-4 h-4 mr-1"/> Tambah Item</Button>
                <div className="flex items-center gap-4">
                  <span className="overline">Diterima</span>
                  <span className="font-mono-num font-bold text-xl text-[var(--hotto-primary)]">{formatIDR(total)}</span>
                </div>
              </div>
            </div>

            <AttachmentField kind="retail-report" value={attachment} onChange={setAttachment} onBusyChange={setUploading} disabled={saving} testId="input-retail-attachment"/>
            <div><Label>Catatan (opsional)</Label><Input value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} data-testid="input-retail-notes"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} disabled={saving || uploading} data-testid="btn-cancel-store-sale">Batal</Button>
            <Button onClick={save} disabled={saving || uploading} data-testid="btn-save-store-sale" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">{saving ? "Menyimpan..." : uploading ? "Menunggu unggahan..." : "Simpan Laporan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v)=>!v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Detail Laporan</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Toko:</span> <b>{detail.store_name}</b></div>
                <div><span className="text-slate-500">Tanggal:</span> {formatDate(detail.date)}</div>
                <div><span className="text-slate-500">Metode:</span> {detail.payment_method}</div>
                <div>Lampiran: <AttachmentLink attachment={detail.attachment} testId="sale-detail-attachment"/></div>
                {detail.notes && <div className="col-span-2"><span className="text-slate-500">Catatan:</span> {detail.notes}</div>}
              </div>
              <div className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50"><TableHead>Produk</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Harga</TableHead><TableHead className="text-right">Subtotal</TableHead></TableRow></TableHeader>
                  <TableBody>{detail.items.map((it,i)=>(<TableRow key={i}><TableCell>{it.product_name}</TableCell><TableCell className="text-right font-mono-num">{it.qty}</TableCell><TableCell className="text-right font-mono-num">{formatIDR(it.unit_price)}</TableCell><TableCell className="text-right font-mono-num">{formatIDR(it.subtotal)}</TableCell></TableRow>))}</TableBody>
                </Table>
              </div>
              <div className="flex justify-end"><span className="overline mr-3">Total</span><span className="font-mono-num font-bold text-lg text-[var(--hotto-primary)]">{formatIDR(detail.total)}</span></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
